import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Globe2, Headphones, Hotel, MapPin, Plane, ShieldCheck, Stamp, Users } from 'lucide-react';
import SEO from '../components/SEO';
import GoogleReviews from '../components/GoogleReviews';
import VideoReviews from '../components/site/VideoReviews';
import Partners from '../components/site/Partners';
import { SitePage, PageHero, Facts, SectionHead, CtaBand } from '../components/site/ui';
import { BRAND, unsplash } from '../components/site/brand';
import '../components/site/founder.css';

const FOUNDER_LD = {
  '@context': 'https://schema.org', '@type': 'Person', name: 'Venkatesh Chinthada', jobTitle: 'Founder & Director',
  worksFor: { '@type': 'TravelAgency', name: 'VK Tours', url: 'https://www.vktours.com' },
  knowsAbout: ['Travel and tourism', 'Corporate travel', 'Hotel operations'],
  image: 'https://www.vktours.com/images/founder-venkatesh-chinthada.jpg?v=2', url: 'https://www.vktours.com/about#founder',
};

const OFFICES = [
  { city: 'Vijayawada', region: 'Andhra Pradesh, India', note: 'Head office', detail: BRAND.address },
  { city: 'Delhi NCR', region: 'India', note: 'North India desk', detail: 'Golden Triangle, Himalayan and North India tours' },
  { city: 'Lincoln, Nebraska', region: 'USA', note: 'US office', detail: 'Support for US travellers and USA tours' },
  { city: 'Maldives', region: 'Maldives', note: 'Maldives desk', detail: 'Maldives holidays and on-ground support' },
];

const SERVICES = [
  [Globe2, 'Holidays', 'Tailor-made and fixed-departure holidays across India and 30+ countries.'],
  [Stamp, 'Visas', 'Document checks and filing for tourist visas, including Australia, Schengen, UK, USA and Dubai.'],
  [Plane, 'Flights', 'Domestic and international air tickets, quoted with and without packages.'],
  [Users, 'Groups', 'Family groups, school trips and fixed group departures with a tour manager.'],
  [Briefcase, 'Corporate & MICE', 'Incentive trips, dealer meets and conferences for companies.'],
  [Headphones, 'On-trip support', 'A real person on WhatsApp and phone while you travel.'],
];

const AboutPage = () => (
  <SitePage>
    <SEO title="About VK Tours · 15 Years of Travel" description="VK Tours is a travel company headquartered in Vijayawada with offices in Delhi NCR, the USA and the Maldives — holidays, visas, flights and group travel for 15+ years." path="/about" jsonLd={FOUNDER_LD} />

    <PageHero
      image={unsplash('photo-1688949078626-a358f500e063', 2000)}
      imageAlt="Maldives island resort"
      crumbs={[{ label: 'Home', to: '/' }, { label: 'About' }]}
      kicker="Since 15+ years"
      title="About"
      accent="VK Tours"
      lede="We’re a travel company from Vijayawada with offices across India, the USA and the Maldives. One team plans your trip, books it, and looks after you until you’re home."
    >
      <Link className="vk-btn vk-btn--hot" to="/tour-packages">Browse packages <ArrowRight size={18} /></Link>
      <Link className="vk-btn vk-btn--ghost" to="/contact-us">Contact us</Link>
    </PageHero>

    <Facts items={[
      { value: '15+', label: 'Years in travel' },
      { value: '10,000+', label: 'Happy travellers' },
      { value: '30+', label: 'Countries covered' },
      { value: '4', label: 'Offices' },
    ]} />

    <section className="vk-section">
      <div className="vk-wrap vk-split">
        <div>
          <p className="vk-kicker">Our story</p>
          <h2 className="vk-display vk-h2" style={{ marginBottom: 20 }}>Travel, handled properly</h2>
          <div className="vk-prose">
            <p>For more than 15 years we’ve planned holidays for families, couples, groups and companies — from a weekend in Goa to three weeks across Europe.</p>
            <p>What hasn’t changed is how we work: clear quotes with flights shown separately, hotels we know, visas checked before they’re filed, and a person you can call while you’re travelling.</p>
            <p>Today our head office in Vijayawada works alongside teams in Delhi NCR, the USA and the Maldives, so we can plan and support trips on the ground.</p>
          </div>
        </div>
        <div className="vk-panel vk-panel--sand">
          <h3>How we work</h3>
          <ul className="vk-ticks">
            <li><ShieldCheck size={18} color="var(--hot)" /> Transparent pricing — package with and without flights</li>
            <li><ShieldCheck size={18} color="var(--hot)" /> Hotels and partners we work with regularly</li>
            <li><ShieldCheck size={18} color="var(--hot)" /> Visa documents reviewed before filing</li>
            <li><ShieldCheck size={18} color="var(--hot)" /> 24×7 WhatsApp support during the trip</li>
            <li><ShieldCheck size={18} color="var(--hot)" /> GST invoices for every booking</li>
          </ul>
        </div>
      </div>
    </section>

    <section className="vk-section vk-section--sand" id="founder" aria-labelledby="founder-title">
      <div className="vk-wrap vk-founder">
        <figure className="vk-founder__photo">
          <img src="/images/founder-venkatesh-chinthada.webp?v=2" alt="Venkatesh Chinthada, Founder and Director of VK Tours" width="900" height="900" loading="lazy" decoding="async" />
          <figcaption><b>Venkatesh Chinthada</b><span>Founder &amp; Director</span></figcaption>
        </figure>
        <div>
          <p className="vk-kicker">Meet the founder</p>
          <h2 id="founder-title" className="vk-display vk-h2" style={{ marginBottom: 12 }}>Venkatesh Chinthada</h2>
          <p className="vk-founder__role">Founder &amp; Director, VK Tours</p>
          <div className="vk-prose">
            <p>Venkatesh has spent 15 years in travel. He began with Tripgrand Travel Services Private Limited and has since built strong relationships with tourism partners across the world. He has extensive experience handling international travel for corporate clients.</p>
            <p>Earlier in his career he operated hotels, so he knows what makes a stay go well from the inside. Having travelled to several countries himself, he plans trips from first-hand experience: the right hotels, sensible routes and partners he trusts on the ground.</p>
          </div>
          <blockquote className="vk-founder__quote">
            <p>“I believe in only two things: good service and customer satisfaction.”</p>
            <cite>Venkatesh Chinthada</cite>
          </blockquote>
          <ul className="vk-founder__exp">
            <li><Plane size={20} aria-hidden="true" /><div><b>15 years in travel</b><span>From Tripgrand Travel Services to VK Tours</span></div></li>
            <li><Briefcase size={20} aria-hidden="true" /><div><b>Corporate international travel</b><span>Trips abroad for corporate clients</span></div></li>
            <li><Hotel size={20} aria-hidden="true" /><div><b>Hotel background</b><span>Operated hotels earlier in his career</span></div></li>
            <li><Globe2 size={20} aria-hidden="true" /><div><b>Travelled widely</b><span>Several countries, seen first-hand</span></div></li>
          </ul>
        </div>
      </div>
    </section>

    <section className="vk-section vk-section--sand vk-partner-sec" id="usa-partner" aria-labelledby="usa-partner-title">
      <div className="vk-wrap">
      <div className="vk-partner">
        <figure className="vk-partner__photo">
          <img src="/images/usa-partner-dacias-king.webp" alt="Dacias King, VK Tours partner in the USA" width="640" height="800" loading="lazy" decoding="async" />
        </figure>
        <div>
          <p className="vk-kicker">Our partner in the USA</p>
          <h2 id="usa-partner-title" className="vk-display vk-h2" style={{ marginBottom: 12 }}>Dacias King</h2>
          <p className="vk-founder__role">USA Partner, VK Tours · Lincoln, Nebraska</p>
          <div className="vk-prose">
            <p>Dacias King is our partner in the United States, working with our office in Lincoln, Nebraska. Together we look after travellers from the USA and plan trips to America for our customers in India.</p>
          </div>
        </div>
      </div>
      </div>
    </section>

    <Partners />

    <section className="vk-section vk-section--ink">
      <div className="vk-wrap">
        <SectionHead light kicker="Where we are" title="Our offices" />
        <div className="vk-feats">
          {OFFICES.map((o) => (
            <div key={o.city} className="vk-feat">
              <MapPin size={26} />
              <h3 style={{ color: '#fff' }}>{o.city}</h3>
              <p style={{ color: 'var(--sun)', fontWeight: 700, marginBottom: 6 }}>{o.note} · {o.region}</p>
              <p>{o.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="vk-section">
      <div className="vk-wrap">
        <SectionHead kicker="What we do" title="One team for the whole trip" />
        <div className="vk-feats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          {SERVICES.map(([Icon, title, text]) => (
            <div key={title} className="vk-feat"><Icon size={28} /><h3>{title}</h3><p>{text}</p></div>
          ))}
        </div>
      </div>
    </section>

    <VideoReviews title="Our travellers, on camera" />
    <GoogleReviews />
    <CtaBand />
  </SitePage>
);

export default AboutPage;
